/**
 * Lazy Manifold (manifold-3d) WASM runtime loader.
 *
 * The exact-CSG kernel used by the Boolean modifier. Loaded once per context
 * (main thread or worker) via a singleton promise — mirrors the pattern used
 * by `wasm/init.ts` for the in-house poseref_wasm module.
 *
 * Manifold ships as an Emscripten module. Vite's `?url` import resolves
 * `manifold.wasm` to a hashed asset URL that both the main thread and the
 * module worker can fetch.
 */

import type { ManifoldToplevel } from "manifold-3d";
import Module from "manifold-3d";
// Vite resolves this to a hashed asset URL at build time; works in the main
// thread and in module workers alike.
import manifoldWasmUrl from "manifold-3d/manifold.wasm?url";

let _promise: Promise<ManifoldToplevel> | null = null;
let _toplevel: ManifoldToplevel | null = null;

function locateManifoldWasm(): string {
  if (
    typeof process !== "undefined" &&
    process.versions?.node &&
    manifoldWasmUrl.startsWith("/@fs/")
  ) {
    return manifoldWasmUrl.slice("/@fs".length);
  }
  return manifoldWasmUrl;
}

/**
 * Returns the Manifold toplevel, initializing it on first call.
 * Safe to call concurrently; the underlying promise is memoized.
 */
export function ensureManifoldReady(): Promise<ManifoldToplevel> {
  if (!_promise) {
    _promise = Module({ locateFile: locateManifoldWasm }).then((mod) => {
      mod.setup();
      _toplevel = mod;
      return mod;
    });
  }
  return _promise;
}

/** Returns the initialized Manifold toplevel, or null if not ready yet. */
export function getManifold(): ManifoldToplevel | null {
  return _toplevel;
}

/** True iff `ensureManifoldReady()` has fully resolved. */
export function isManifoldReady(): boolean {
  return _toplevel !== null;
}
